<template>
  <div>
    <v-dialog @keydown.esc="dialogOpen=false" v-model="dialogOpen" persistent max-width="816px">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            small
            color="transparent"
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="dialogTitle">{{ $t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.title') }}</v-card-title>
        <v-card-text class="d-flex flex-column justify-center">
          <div class="text-right">
            <Button @click="openAddRelatedPayOrdersDialog(relation)" class="blueButton" v-if="bankReconciliationPermissions['create-pay-order']">
              <v-icon>mdi-plus</v-icon>
              <span>{{ $t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.add') }}</span>
            </Button>
          </div>
          <div v-if="table.data.length">
            <v-data-table
              :loading-text="table.loadingText"
              :loading="table.loading"
              :headers="table.headers"
              :items="table.data"
              :options="table.options"
              hide-default-footer
              class="table"
            >
              <template v-slot:[`item._id`]="{ item }">
                <div>
                  <a @click="openPayOrderDetailsPanel(item)" class="link">{{ item._id }}</a>
                </div>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <div class="text-right">{{ item.amount | toFix2 | numberWithComma | prefix('$') }}</div>
              </template>
              <template v-slot:[`item.date`]="{ item }">
                <div>
                  {{ $moment(item.date).format('DD/MM/YYYY') }}
                </div>
              </template>
              <template v-slot:[`item.paymentMethod`]="{ item }">
                <div>
                  {{ PAYORDER_PAYMENT_TYPES[item.paymentMethod] }}
                </div>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <div>
                  {{ STATUS_TYPES[item.status] }}
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }" v-if="bankReconciliationPermissions['delete-pay-order']">
                <div>
                  <v-btn icon @click="deleteRelatedPayOrder(item._id)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
          <div v-else class="noInfo mt-2">
            {{ $t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.noInfo') }}
          </div>
          <p class="sumText">{{ $t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.sum', { sum: formatCurrency(amountSum) }) }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <AddRelatedPayOrdersViewer
      ref="addRelatedPayOrdersViewer"
      @updateRelations="$emit('updateRelations')"
      @updateRelatedPayOrders="loadRelatedPayOrders()"
    />

    <PayOrderDetails
      :isOpen.sync="payOrderDetailsPanelOpen"
      v-bind:selectedPayOrder="selectedPayOrder"
      @close="resetPayOrderDetails()"
    />
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import sumBy from 'lodash/sumBy'

import formatCurrency from '@/utils/formatCurrency'
import PayOrderDetails from '@/components/sidePanels/PayOrderDetails'
import Button from '@/components/elements/Button'
import AddRelatedPayOrdersViewer from '@/components/modals/AddRelatedPayOrdersViewer'

export default {
  name: 'RelatedPayOrdersViewer',
  props: {},
  emits: ['updateRelations'],
  components: {
    Button,
    PayOrderDetails,
    AddRelatedPayOrdersViewer
  },
  data () {
    return {
      overlay: false,
      dialogOpen: false,
      table: {
        loadingText: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.loadingText'),
        loading: false,
        headers: [
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.id'), value: '_id', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.amount'), value: 'amount', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.date'), value: 'date', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.paymentMethod'), value: 'paymentMethod', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.status'), value: 'status', sortable: false },
          { text: this.$i18n.t('expenses.bankReconciliation.relations.relatedPayOrdersDialog.table.headers.actions'), value: 'actions', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: -1,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [],
          sortDesc: []
        }
      },
      selectedPayOrder: {},
      payOrderDetailsPanelOpen: false,
      STATUS_TYPES: {
        created: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.created'),
        pending_payment: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.pendingPayment'),
        paid: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.statusTypes.paid')
      },
      PAYORDER_PAYMENT_TYPES: {
        balance: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.paymentTypes.balance'),
        credit: this.$i18n.t('expenses.bankReconciliation.transactions.relationsPanel.paymentTypes.credit')
      },
      relation: undefined
    }
  },
  watch: {
    dialogOpen: function (val) {
      if (val) {
        this.loadRelatedPayOrders()
      }
    }
  },
  computed: {
    amountSum: function () {
      return sumBy(this.table.data, 'amount')
    },
    bankReconciliationPermissions () {
      return this.$store.state.login.permissions['expenses'].components['financial-movements'].actions
    }
  },
  methods: {
    openDialog (relation) {
      this.relation = relation
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    loadRelatedPayOrders: async function () {
      this.table.loading = true
      try {
        const response = await this.$api.get(`/v1/bank-reconciliation/${this.relation._id}/orders`)
        this.table.data = !isEmpty(response.data) ? response.data[0].orders : []
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    deleteRelatedPayOrder: async function (payOrderId) {
      this.overlay = true
      try {
        await this.$api.del(`/v1/bank-reconciliation/${this.relation._id}/orders/${payOrderId}`)
        // Reload related payorders
        this.loadRelatedPayOrders()
        // Reload relations
        this.$emit('updateRelations')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    formatCurrency,
    openPayOrderDetailsPanel (item) {
      this.selectedPayOrder = item
      this.payOrderDetailsPanelOpen = true
    },
    resetPayOrderDetails () {
      this.selectedPayOrder = {}
    },
    openAddRelatedPayOrdersDialog (relation) {
      this.$refs.addRelatedPayOrdersViewer.openDialog(relation)
    }
  }
}
</script>

<style lang="scss" scoped>
  .dialogTitle {
    display: flex;
    justify-content: center;
    padding-top: 0 !important;
    font-size: 22px !important;
    color: #01D28E;
  }

  .table {
    overflow-y: auto;
    max-height: 65vh;

    & tr {
      border: 1px solid #ACABAB;
      border-radius: 7px;

      &:first-child {
        border-left: 1px solid #ACABAB;
        border-top-left-radius: 7px;
        border-bottom-left-radius: 7px;
      }
    }

    & td {
      border-top: 1px solid #ACABAB;
      border-bottom: 1px solid #ACABAB;

      &:last-child {
        border-right: 1px solid #ACABAB;
        border-top-right-radius: 7px;
        border-bottom-right-radius: 7px;
      }
    }

    & thead tr:last-child th {
      border-bottom: none !important;
    }
  }

  .sumText {
    margin-top: 23px;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #3F3F3F;
  }

  .noInfo {
    border: 1px solid #ACABAB;
    border-radius: 7px;
    padding-top: 22px;
    padding-bottom: 22px;
    text-align: center;
    line-height: 19px;
    color: #ACABAB;
  }

  .blueButton {
    background-color: #0F6FFF !important;
  }
</style>
